import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { useTheme } from '@mui/material/styles';

/**
 * Empty-state placeholder for empty lists, no-search-results, no-permission, etc.
 *
 * Usage:
 *   <EmptyState />                                      // generic "No data"
 *   <EmptyState message="No customers yet" />
 *   <EmptyState icon={<MyIcon/>} title="Nothing here" message="..." action={<Button>Add</Button>} />
 *   <EmptyState size="small" />                         // compact, sits inside small cards
 */
const EmptyState = ({
    icon,
    title,
    message = 'No data',
    description,
    action,
    size = 'medium',
    sx = {}
}) => {
    const theme = useTheme();
    const iconSize = size === 'small' ? 36 : size === 'large' ? 80 : 56;
    const pad = size === 'small' ? 2 : size === 'large' ? 6 : 4;

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: pad,
                px: 2,
                color: theme.palette.text.secondary,
                ...sx
            }}
        >
            <Stack spacing={1.25} alignItems="center" sx={{ maxWidth: 360, textAlign: 'center' }}>
                <Box
                    sx={{
                        width: iconSize + 16,
                        height: iconSize + 16,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.action?.hover || 'rgba(0,0,0,0.04)',
                        color: theme.palette.text.disabled || theme.palette.text.secondary
                    }}
                >
                    {icon || <InboxOutlinedIcon sx={{ fontSize: iconSize }} />}
                </Box>

                {title && (
                    <Typography variant={size === 'small' ? 'subtitle1' : 'h4'} sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
                        {title}
                    </Typography>
                )}

                <Typography variant={size === 'small' ? 'caption' : 'body2'} sx={{ color: theme.palette.text.secondary }}>
                    {message}
                </Typography>

                {description && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled || theme.palette.text.secondary, mt: 0.5 }}>
                        {description}
                    </Typography>
                )}

                {action && <Box sx={{ mt: 1 }}>{action}</Box>}
            </Stack>
        </Box>
    );
};

EmptyState.propTypes = {
    icon: PropTypes.node,
    title: PropTypes.node,
    message: PropTypes.node,
    description: PropTypes.node,
    action: PropTypes.node,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    sx: PropTypes.object
};

export default EmptyState;
